import { useAppState } from '~/app/contexts/app-context';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { createNewDataSection, filterConditionalEntries } from '~/app/utils/data-sections';
import { getTechnicalDataItemsConfiguredCar } from '~/app/utils/get-technical-data-items';
import { ConfiguredCarTechnicalDataQuery } from '~/graphql/__generated__/graphql';

export function useConfiguredCarTechnicalDataSections(data: ConfiguredCarTechnicalDataQuery) {
	const { market } = useMarketConfig();
	const { translations } = useAppState();
	const { i18n } = useI18n();
	const td = data.configuredCar.technicalDataV2;
	if (!td) return [];
	const driveTrain = td.modelAttributes?.driveTrain ?? 'BEV';

	const {
		engineData,
		performanceData,
		technicalData,
		drivelineData,
		landingGearData,
		brakeSystemData,
		steeringSystemData,
		weightsData,
		trailerWeightData,
		volumeData,
		consumptionData,
		noiseData,
		insuranceData,
		emissionData,
	} = getTechnicalDataItemsConfiguredCar(td, market, driveTrain, i18n, translations);

	return filterConditionalEntries([
		createNewDataSection(i18n('ngc.technicalData.engine'), [engineData], translations),
		createNewDataSection(i18n('ngc.technicalData.performanceData'), performanceData, translations),
		createNewDataSection(
			driveTrain === 'ICE' || driveTrain === 'MHEV'
				? i18n('ngc.technicalData.combustionDrive')
				: i18n('ngc.technicalData.electricDrive'),
			technicalData,
			translations,
		),
		createNewDataSection(i18n('ngc.technicalData.driveline'), drivelineData, translations),
		createNewDataSection(i18n('ngc.technicalData.landingGear'), landingGearData, translations),
		createNewDataSection(i18n('ngc.technicalData.brakeSystem'), brakeSystemData, translations),
		createNewDataSection(i18n('ngc.technicalData.steeringSystem'), steeringSystemData, translations),
		createNewDataSection(i18n('ngc.technicalData.weights'), weightsData, translations),
		market.toUpperCase() !== 'TW' &&
			createNewDataSection(i18n('ngc.technicalData.trailerWeightLimit'), trailerWeightData, translations),
		createNewDataSection(i18n('ngc.technicalData.volumes'), volumeData, translations),
		market.toUpperCase() !== 'US' &&
			createNewDataSection(i18n('ngc.technicalData.consumption'), consumptionData, translations),
		createNewDataSection(i18n('ngc.technicalData.noiseLevel'), noiseData, translations),
		createNewDataSection(i18n('ngc.technicalData.insuranceClasses'), insuranceData, translations),
		market.toUpperCase() !== 'US' &&
			createNewDataSection(i18n('ngc.technicalData.consumptionAndEmission'), emissionData, translations),
	]).filter((section) => section.entries.length > 0);
}
